import classcat from "classcat";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  IconBasket,
  IconExclamationCircle,
  IconTickCircle,
} from "@touchtech/web-baselayer-ui";

import { Image } from "@is/components/Image";
import { MediaVariants, TranslationContext } from "@is/constants";
import { useOutsideSyntheticClick, useWidgetProductDetails } from "@is/hooks";
import { MediaOrderingRule, ProductItem } from "@is/types";
import { sortProductMedia } from "@is/utils";

import css from "./HTWIQuickAddToBasket.module.css";

const CLOSING_ANIMATION_DURATION = 300;

type Props = {
  item: ProductItem;
  onBasketAdded?: () => void;
};

const sortingOrder = MediaOrderingRule.Widget;

const HTWIQuickAddToBasket = ({ item, onBasketAdded }: Props) => {
  const { t } = useTranslation(TranslationContext.ProductDetails);

  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => setIsClosing(true), []);

  const {
    selectedColorVariantId,
    selectedSize,
    selectedLength,
    isBasketItemsPending,
    isAdded,
    isLengthRequired,
    showOutOfStockNotification,
    showUnavailableSlash,
    variants: colorVariants,
    sizes,
    lengths,
    mediaUrl,
    variantsWithSelectedSize: colorVariantsWithSelectedSize,
    sizesForColorVariant,
    lengthsForColorVariant,
    updateViewState,
    handleQuickAddToBasketWrapperClick,
    handleSelectVariant,
    handleSelectSize,
    handleSelectLength,
    handleBasketButtonClick,
  } = useWidgetProductDetails({ item, onReset: handleClose, onBasketAdded });

  const outsideEventRef = useOutsideSyntheticClick((event) => {
    if (item) {
      event.preventDefault();
      event.stopPropagation();
      setIsClosing(true);
    }
  });

  const bothSizesAndLengths = useMemo(
    () => sizes.length && lengths.length,
    [sizes, lengths]
  );

  const addToBasketButtonContent = useMemo(() => {
    let text = t("add to basket");

    let icon = (
      <IconBasket className={classcat([css.basketIcon, "text-label-base"])} />
    );

    if (showOutOfStockNotification) {
      icon = (
        <IconExclamationCircle
          className={classcat([css.basketIcon, "text-label-base"])}
        />
      );
      text = t("out of stock");
    } else if (isAdded) {
      icon = (
        <IconTickCircle
          className={classcat([css.basketIcon, "text-label-base"])}
        />
      );
      text = t("added!");
    } else if (!selectedSize) {
      icon = null;
      text = t("select size");
    } else if (isLengthRequired && !selectedLength) {
      icon = null;
      text = t("select length");
    }

    return (
      <>
        {icon} {text}
      </>
    );
  }, [
    t,
    isAdded,
    showOutOfStockNotification,
    isLengthRequired,
    selectedLength,
    selectedSize,
  ]);

  useEffect(() => {
    if (!isClosing) {
      return;
    }

    const timeout = setTimeout(() => {
      updateViewState({ quickAddToBasketItem: null });
    }, CLOSING_ANIMATION_DURATION);

    return () => {
      clearTimeout(timeout);
    };
  }, [isClosing, updateViewState]);

  return (
    <div
      className={classcat([css.container, { [css.closing]: isClosing }])}
      onClick={handleQuickAddToBasketWrapperClick}
    >
      <div
        ref={outsideEventRef}
        className={classcat([css.styleDetails, { [css.closing]: isClosing }])}
      >
        <div className={classcat([css.styleColumn, css.styleImageContainer])}>
          <Image
            src={mediaUrl}
            mediavariant={MediaVariants.MEDIUM}
            layout="contain"
          />
        </div>
        <div className={classcat([css.styleColumn, css.styleBasket])}>
          <div className={css.transparentBackground} />

          <div className={classcat(["text-headline-xl", css.title])}></div>

          <div>
            {colorVariants.length > 1 && (
              <div
                className={classcat([css.colorVariantSelector, css.scrollArea])}
              >
                {colorVariants?.map(({ id, media }) => (
                  <button
                    key={id}
                    data-id={id}
                    className={classcat([
                      {
                        [css.selected]: selectedColorVariantId === id,
                      },
                      css.colorVariantItem,
                    ])}
                    onClick={handleSelectVariant}
                  >
                    <Image
                      mediavariant={MediaVariants.THUMBNAIL}
                      src={sortProductMedia(media, sortingOrder)[0]?.url}
                      layout="cover"
                    />

                    {!colorVariantsWithSelectedSize.includes(id) &&
                    showUnavailableSlash ? (
                      <div className={css.slash} />
                    ) : null}
                  </button>
                ))}
              </div>
            )}

            {!!sizes.length && (
              <div
                className={classcat([
                  css.sizeAndLengthSelector,
                  css.scrollArea,
                ])}
              >
                {bothSizesAndLengths ? (
                  <div className={css.sizeLabel}>{t("Size")}</div>
                ) : null}
                {sizes.map((value) => (
                  <button
                    key={value}
                    data-id={value}
                    onClick={handleSelectSize}
                    className={classcat([
                      {
                        [css.selectedSizeAndLength]: selectedSize === value,
                        [css.unavailableSizeAndLength]:
                          !sizesForColorVariant.includes(value),
                        [css.fullWidth]: sizes.length === 1,
                      },
                      css.sizeAndLength,
                    ])}
                  >
                    <div>{value}</div>
                  </button>
                ))}
              </div>
            )}

            {!!lengths.length && (
              <div
                className={classcat([
                  css.sizeAndLengthSelector,
                  css.scrollArea,
                ])}
              >
                {bothSizesAndLengths ? (
                  <div className={css.sizeLabel}>{t("Length")}</div>
                ) : null}
                {lengths.map((value) => (
                  <button
                    key={value}
                    data-id={value}
                    onClick={handleSelectLength}
                    className={classcat([
                      {
                        [css.selectedSizeAndLength]: selectedLength === value,
                        [css.unavailableSizeAndLength]:
                          !lengthsForColorVariant.includes(value),
                        [css.fullWidth]: lengths.length === 1,
                      },
                      css.sizeAndLength,
                    ])}
                  >
                    <div>{value}</div>
                  </button>
                ))}
              </div>
            )}

            <div className={css.basketButtonContainer}>
              <Button
                width="full"
                sizing="sm"
                className={classcat([
                  css.basketButton,
                  {
                    [css.added]: isAdded,
                    [css.outOfStock]: showOutOfStockNotification,
                    [css.notSelectedSizeOrLength]:
                      !selectedSize || (isLengthRequired && !selectedLength),
                  },
                ])}
                onClick={handleBasketButtonClick}
                state={isBasketItemsPending ? "pending" : undefined}
              >
                {addToBasketButtonContent}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HTWIQuickAddToBasket;
